import * as React from 'react'
import { ChangeEvent, ReactElement, useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import LinearProgress from '@mui/material/LinearProgress'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'

import { Pagination } from '@sales-center/components'
import { useSearchBillTos } from '@user/store/entityLocations'
import { selectCurrentUser } from '@user/store/authorization/userSlice'
import { TidalBillToRadioList } from './BillToRadioList/TidalBillToRadioList'
import IconButton from '@mui/material/IconButton'

type Props = {
  branchId?: string
  delayTimeMS?: number
  label?: ReactElement | string
  locationId?: string
  onChange: (locationId: string, locationCode?: string) => void
  onClearClick: () => void
  placeHolderText?: string
  isInValid?: boolean
  error?: string | undefined
}

const takeSize = 3

export function TidalBillToSelect(props: Props) {
  const { onChange, onClearClick, isInValid, error } = props

  const [selectedBillToId, setSelectedBillToId] = useState<string>()

  const currentUser = useSelector(selectCurrentUser)

  const branchIds = React.useMemo(
    () => (currentUser.isAdmin ? '' : currentUser.branches?.map((b) => b.id)),
    [currentUser.branches, currentUser.isAdmin],
  )

  const {
    count,
    items,
    loading,
    onChangePage,
    onChangeSearchText,
    onClearSearchText,
    page,
    pages,
    searchText,
  } = useSearchBillTos('', takeSize, branchIds)

  const handleChangeRadio = useCallback(
    (billToId: string, locationCode: string) => {
      setSelectedBillToId(billToId)
      onChange(billToId, locationCode)
    },
    [onChange],
  )

  const handleChangeSearchText = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChangeSearchText(e)
      const { value } = e.target
      if (value === '') {
        onClearClick()
        setSelectedBillToId(undefined)
      }
    },
    [onChangeSearchText, onClearClick],
  )

  const handleClearSearchText = useCallback(() => {
    onClearSearchText()
    onClearClick()
    setSelectedBillToId(undefined)
  }, [onClearSearchText, onClearClick])

  const resultsCountLabel = loading
    ? null
    : !items || !searchText
      ? null
      : count === 0
        ? 'No Locations Found'
        : `${count} Location${count === 1 ? '' : 's'} Found`

  return (
    <>
      <TextField
        value={searchText}
        onChange={handleChangeSearchText}
        fullWidth
        required
        spellCheck={false}
        error={isInValid}
        helperText={isInValid ? error : undefined}
        InputProps={{
          inputProps: {
            maxLength: 30,
            'data-test-id': 'billToSearchInput',
          },
          placeholder: 'Bill-To Location *',
          endAdornment: searchText && (
            <IconButton
              sx={{ mr: -1 }}
              onClick={handleClearSearchText}
              data-test-id="billToSearchClearBtn"
            >
              <CloseIcon />
            </IconButton>
          ),
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="medium" />
            </InputAdornment>
          ),
        }}
      />
      {loading ? (
        <LinearProgress />
      ) : (
        <>
          <Typography mb={1} mt={2} variant="body2">
            {resultsCountLabel}
          </Typography>
          {items && count > 0 && (
            <>
              <TidalBillToRadioList
                billToId={selectedBillToId}
                locations={items}
                onChange={handleChangeRadio}
              />
              <Box p={1} display="flex">
                <Pagination
                  page={page}
                  pages={pages}
                  onChangePage={onChangePage}
                />
              </Box>
            </>
          )}
        </>
      )}
    </>
  )
}
